/**
 * Centralized Drawer Component
 * 
 * Main drawer component that uses the global drawer stack and Mantine's native
 * compound components with swipe gesture support and consistent close functionality.
 * 
 * When used with ResponsiveModal, syncs drawer close events back to the modal stack
 * for proper URL synchronization.
 */

import * as React from "react";
import { Drawer } from "@mantine/core";
import { cn } from "@/lib/utils.js";
import { useGlobalDrawerStack } from "../context.js";
import { useTouchGestures } from "../hooks/useTouchGestures.js";
import { DrawerHeader } from "./DrawerHeader.js";
import { DrawerContent } from "./DrawerContent.js";
import {
  mapSideToPosition,
  mapHeightToSize,
  getViewportConstrainedStyles,
} from "../utils/drawerPosition.js";
import type { DrawerConfig } from "../types.js";

interface CentralizedDrawerProps {
  drawerId: string;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  height?: string;
  zIndex?: number;
  enableSwipeGesture?: boolean;
  showCloseButton?: boolean;
  className?: string;
  children: React.ReactNode;
  onClose?: () => void;
}

export const CentralizedDrawer: React.FC<CentralizedDrawerProps> = ({
  drawerId,
  title,
  description,
  side = "bottom",
  // No default height: bottom/top sheets fit their content (capped at 85dvh via
  // getViewportConstrainedStyles) so short menus/pickers don't leave a large
  // empty gap. Pass an explicit height (e.g. "90vh") to opt into a fixed size.
  height,
  zIndex,
  enableSwipeGesture = true,
  showCloseButton = true,
  className,
  children,
  onClose,
}) => {
  const { drawerStack, getDrawerConfig } = useGlobalDrawerStack();
  
  // Safety check - drawerStack should always be defined if provider is set up correctly
  if (!drawerStack) {
    console.error('CentralizedDrawer: drawerStack is undefined. Ensure GlobalDrawerStackProvider wraps this component.');
    return null;
  }
  
  const config = getDrawerConfig(drawerId);

  // Use config values or fallback to props
  const finalConfig: DrawerConfig = React.useMemo(
    () => ({
      id: drawerId,
      title: config?.title || title,
      description: config?.description || description,
      side: config?.side || side,
      height: config?.height || height,
      zIndex: config?.zIndex ?? zIndex,
      enableSwipeGesture: config?.enableSwipeGesture ?? enableSwipeGesture,
      showCloseButton: config?.showCloseButton ?? showCloseButton,
      className: config?.className || className,
    }),
    [config, drawerId, title, description, side, height, zIndex, enableSwipeGesture, showCloseButton, className]
  );

  // Handle drawer close with optional callback
  const handleDrawerClose = React.useCallback(() => {
    drawerStack.close(drawerId);
    onClose?.();
  }, [drawerStack, drawerId, onClose]);

  // Touch gesture handling
  const { handleTouchStart, handleTouchMove, handleTouchEnd, swipeOffset, touchStart, touchCurrent } =
    useTouchGestures(finalConfig, handleDrawerClose);

  // Map side to Mantine position
  const position = React.useMemo(() => mapSideToPosition(finalConfig.side), [finalConfig.side]);

  // Map height to Mantine size
  const size = React.useMemo(
    () => mapHeightToSize(finalConfig.height, finalConfig.side),
    [finalConfig.height, finalConfig.side]
  );

  // A bottom/top sheet with no explicit height sizes to its content (capped).
  const isAutoHeight = React.useMemo(
    () => !finalConfig.height || finalConfig.height === "auto",
    [finalConfig.height]
  );

  // Get viewport-constrained styles
  const viewportStyles = React.useMemo(
    () => getViewportConstrainedStyles(finalConfig.side, isAutoHeight),
    [finalConfig.side, isAutoHeight]
  );

  return (
    <Drawer
      {...drawerStack.register(drawerId)}
      withCloseButton={false}
      position={position}
      size={size}
      zIndex={finalConfig.zIndex}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      classNames={{
        content: cn(
          "flex flex-col p-0 overflow-hidden",
          // Rounded sheet edge (top for bottom sheets, bottom for top sheets)
          finalConfig.side === "bottom" && "rounded-t-2xl",
          finalConfig.side === "top" && "rounded-b-2xl",
          // Mobile-optimized touch scrolling
          "touch-pan-y",
          // Add visual feedback for swipe gestures
          finalConfig.side === "bottom" &&
            finalConfig.enableSwipeGesture &&
            touchStart &&
            touchCurrent &&
            "transition-transform duration-150",
          finalConfig.className
        ),
      }}
      styles={{
        content: {
          // Ensure proper mobile viewport constraints
          ...viewportStyles,
          WebkitOverflowScrolling: "touch",
          scrollBehavior: "smooth",
          // Add visual feedback for swipe gestures
          ...(finalConfig.side === "bottom" &&
          finalConfig.enableSwipeGesture &&
          touchStart &&
          touchCurrent
            ? {
                transform: `translateY(${swipeOffset}px)`,
              }
            : {}),
        },
      }}
    >
      <DrawerHeader config={finalConfig} />
      <DrawerContent>{children}</DrawerContent>
    </Drawer>
  );
};
